<template>
  <div
    class="product-block flex-column"
    :class="{
      'with-hover': props.object.images.length > 1,
      [props.object.slug]: true,
    }"
  >
    <DebugBlock>
      {{ props.object.remote_id }}<br />
      {{ props.object.deployed.toISOString() }}<br />
      {{
        shop.objects.categories
          .filter((c) => props.object.categories.includes(c.id))
          .map((c) => c.name)
          .join(", ")
      }}
    </DebugBlock>
    <a class="desktop-image" href="#">
      <ThumbnailedImage
        v-if="props.object.images.length > 0"
        @click.prevent="handleImageClick"
        :images="props.object.images[imageCycle % props.object.images.length]"
        :alt="props.object.name"
        class="primary-image"
      />
      <div v-else class="empty"></div>
      <ThumbnailedImage
        v-if="props.object.images.length > 1"
        @click.prevent="handleImageClick"
        :images="
          props.object.images[(imageCycle + 1) % props.object.images.length]
        "
        :alt="props.object.name"
        class="alternate-image"
      />
      <div class="quick-look">
        <Button @click.prevent="showDialog">
          <span class="quick-look-text">Quick Look</span>
        </Button>
      </div>
    </a>
    <Dialog
      v-if="dialogAvailable"
      v-model:visible="dialogAvailable"
      :modal="true"
      :dismissable-mask="true"
      class="simple-dialog"
      ref="dialog"
    >
      <template #container>
        <ProductPopover :object="props.object" :back="props.back" />
      </template>
    </Dialog>
    <div class="meta-row flex-row">
      <ProductPrice :object="object" class="flex-static" />
      <RouterLink
        :to="{
          name: 'product-detail',
          params: { slug: props.object.slug },
          query: { back: props.back },
        }"
        class="name flex-stretch"
      >
        {{ props.object.name }}
      </RouterLink>
    </div>
    <div class="add-row flex-row flex-align-center">
      <AddToCart
        @update:quantity="emits('update:quantity', $event)"
        :quantity="quantity"
        :show-quantity="props.showQuantity"
        :show-wishlist="props.showWishlist"
        :object="props.object"
        @added="emits('added', $event)"
      />
      <span class="flex-stretch" />
      <!-- <span class="muted">{{
        new Date(props.object.date_added).toLocaleDateString()
      }}</span> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import ThumbnailedImage from "@/components/ThumbnailedImage.vue";
import AddToCart from "@/components/shop/AddToCart.vue";
import type { Product } from "@/stores/defs/shop_defs";
import { useShopStore } from "@/stores/shop";
import { useWindowStore } from "@/stores/window";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import DebugBlock from "../DebugBlock.vue";
import ProductPopover from "./ProductPopover.vue";
import ProductPrice from "./ProductPrice.vue";

const props = withDefaults(
  defineProps<{
    object: Product;
    quantity?: number;
    showQuantity?: boolean;
    showWishlist?: boolean;
    back?: string;
  }>(),
  { showQuantity: false, showWishlist: true },
);
const emits = defineEmits(["added", "update:quantity"]);
const window = useWindowStore();
const router = useRouter();
const shop = useShopStore();

const dialog = ref<HTMLDialogElement>();
const dialogAvailable = ref(false);
const imageCycle = ref(0);

// preventing it from loading immediately so
// that the related products don't need to load
async function showDialog() {
  dialogAvailable.value = true;
}

function handleImageClick() {
  if (window.width > 800) {
    router.push({
      name: "product-detail",
      params: { slug: props.object.slug },
      query: { back: props.back },
    });
  } else {
    if (imageCycle.value) {
      imageCycle.value = 0;
    } else {
      imageCycle.value = 1;
    }
  }
}
</script>

<style scoped>
.product-block {
  max-width: 384px;
  min-width: 100px;
}
.product-block > a {
  display: grid;
}
.product-block img,
.product-block .empty,
.product-block .quick-look {
  width: 100%;
  object-fit: cover;
  aspect-ratio: 8/10;
  margin: auto;
  grid-row: 1/1;
  grid-column: 1/1;
}
.product-block .name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.product-block .meta-row {
  margin: var(--2XS, 8px) 0;
}
.product-block .meta-row,
.product-block .add-row {
  gap: var(--2XS, 8px);
}

.mobile-image {
  display: none;
}

.quick-look {
  position: relative;
  display: none;
  z-index: 3;
  pointer-events: none;
}
.quick-look .p-button {
  position: absolute;
  bottom: 0px;
  text-transform: uppercase;
  width: 100%;
  background: rgba(0, 0, 0, 0);
  pointer-events: all;
  padding: 14px;
}
.quick-look .p-button span {
  padding: 10px;
  width: 100%;
  color: white;
  font-weight: 300;
  letter-spacing: 0.5px;
  background: rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
}

.quick-look .p-button span:hover {
  background: var(--White, #fff);
  color: var(--Black, #000);
}

.alternate-image {
  transition:
    opacity 100ms,
    border-radius 60ms,
    transform 60ms;
  opacity: 0;
  z-index: 2;
  transform: scale(1);
}

.muted {
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.8em;
}

@media (min-width: 800px) {
  .product-block:hover {
    .quick-look {
      display: block;
    }
    &.with-hover {
      .alternate-image {
        opacity: 1;
        border-radius: 0;
        transform: scale(1);
      }
      .primary-image {
        opacity: 0;
      }
    }
  }
}
</style>
